import MasterCardIcon from 'assets/icons/payments/master-card-icon.svg';
import PaytoIcon from 'assets/icons/payments/payto-icon-2.svg';
import VisaIcon from 'assets/icons/payments/visa-icon.svg';
import PaymentSuccessfullyImage from 'assets/icons/txn-successful.svg';
import { fetchTransactionInformation } from 'components/Transactions/func';
import ButtonCommon from 'components/common/ButtonCommon';
import {
  CREADIT_CARD_TYPE_MASTERCARD,
  CREADIT_CARD_TYPE_VISA,
  HHMT_ACCOUNT_NUMBER,
  HHMT_BSB,
  HHMT_EMAIL_PAY,
  HHMT_NAME,
  METHOD_DEPOSIT,
  METHOD_EPAYMENT,
  METHOD_PAYID,
  PAYMENT_ACCOUNT_FIELD,
  PAYMENT_ACCOUNT_NUMBER_FIELD,
  PAYMENT_AMOUNT_FIELD,
  PAYMENT_BSB_FIELD,
  PAYMENT_BY_DEPOSIT_NUMBER_NAME,
  PAYMENT_CREADIT_CARD_NO,
  PAYMENT_CREADIT_CARD_TYPE,
  PAYMENT_EMAIL_FIELD,
  PAYMENT_METHOD_TEXT_REFERENCE,
  PAYMENT_REFERENCE_FIELD,
  REMOX_ACCOUNT_NUMBER,
  REMOX_BSB,
  REMOX_EMAIL_PAY,
  REMOX_NAME,
  STATUS_PROCESSING,
  STATUS_SUBMITTED,
  STATUS_SUCCESS,
  TRANSACTIONS_PREFIX,
} from 'constants';
import {
  format,
  formatter,
  masterCardNumberValid,
  visaNumberValid,
} from 'helpers';
import useClarity from 'hooks/clarity/useClarity';
import useTheme from 'hooks/useTheme';
import { useEffect, useRef, useState } from 'react';
import {
  SET_ADD_TRANSACTION_CURRENT_STEP,
  SET_CURRENT_RECEIVER,
  SET_TRANSACTION_DETAILS,
} from 'store/action';
import styled from 'styled-components';
import { numberUtils, stringUtils } from 'utils/func';

import NotifyPayIcon from 'assets/icons/notify-pay-icon.svg';
import {
  default as HowToHHMTIcon,
  default as HowToREMOXIcon,
} from 'assets/ver2/icons/question-fill-icon.svg';
import InstructionPopup from 'components/Transactions/components/InstructionPopup';
import InstructionPopupMobile from 'components/Transactions/components/InstructionPopup/mobile';
import {
  DESKTOP_MIN_WIDTH,
  HHMT_EMAIL,
  HHMT_PHONE,
  HHMT_PHONE_2,
  METHOD_PAYTO,
  REMOX_EMAIL,
  REMOX_PHONE,
  REMOX_PHONE_2,
} from 'constants';
import { CONTACT_PHONE_NUMBER_FORMAT } from 'constants/format';
import {
  FORM_ADD_PAYTO_ACCOUNT_NUMBER_NAME,
  FORM_ADD_PAYTO_BANK_NAME_NAME,
  FORM_ADD_PAYTO_PAYID_MOBILE_EMAIL_NAME,
} from 'features/to/const';
import Cookies from 'helpers/cookies';
import { MAKE_PAYMENT_KEY } from 'hooks/form-add-transaction/useAddTransaction';
import useFormAddTransactionStore from 'hooks/form-add-transaction/useFormAddTransactionStore';
import useCheckServiceAlerts from 'hooks/service-alerts/useCheckServiceAlerts';
import useServiceAlertsConfig from 'hooks/service-alerts/useServiceAlertsConfig';
import useServiceAlertsStore from 'hooks/service-alerts/useServiceAlertsStore';
import useAuth from 'hooks/useAuth';
import { useDetectDevice } from 'hooks/useDetectDevice';
import useLang from 'hooks/useLang';
import useQuery from 'hooks/useQuery';
import { useNavigate } from 'react-router-dom';
import FormInputCopy from '../FormInputCopy';
import FormInputCopyHighlight from '../FormInputCopyHighlight';
import { ActionWrapper, NextWrap } from '../Remittance/ver2';

const TransactionSummary = ({ refNumber, totalPayAmount, cardInformation }) => {
  const { t } = useLang();
  const { isHHMTTheme } = useTheme();
  const { token } = useAuth();
  const { isMobile, isTablet, isDesktop } = useDetectDevice();
  const navigate = useNavigate();
  const {
    dispatch,
    resetAddTransactionInformation,
    paytoAccountInformation,
    addTransactionInformation,
  } = useFormAddTransactionStore();
  const { paymentMethod: paymentStore } = addTransactionInformation;

  const [isOpenInstructionPopup, setOpenInstructionPopup] = useState(false);

  const { clarityAPI } = useClarity();
  const stopTimeoutRef = useRef(null);
  useEffect(() => {
    stopTimeoutRef.current = setTimeout(() => clarityAPI.stop(), 2 * 60 * 1000);
    return () => clearTimeout(stopTimeoutRef.current);
  }, []);

  const query = useQuery();
  const paymentStatus = query.get('paymentStatus');

  const { serviceAlertsByPayments: serviceAlertsStore } =
    useServiceAlertsStore();
  const { checkByPaymentMethod } = useCheckServiceAlerts();
  const { checkServiceAlertsOfPaymentMethodItemDisable } =
    checkByPaymentMethod || {};

  const { paymentMethod } = useServiceAlertsConfig();

  const isAgreementByBSB =
    !paytoAccountInformation?.[FORM_ADD_PAYTO_PAYID_MOBILE_EMAIL_NAME];
  const paymentMethodInfo = isAgreementByBSB
    ? `${
        paytoAccountInformation?.bankCode ||
        paytoAccountInformation?.[FORM_ADD_PAYTO_BANK_NAME_NAME]
      }${paytoAccountInformation?.[FORM_ADD_PAYTO_ACCOUNT_NUMBER_NAME]}
  `
    : `PAYID\n(${paytoAccountInformation?.[FORM_ADD_PAYTO_PAYID_MOBILE_EMAIL_NAME]})`;

  const isDisplayInstructionAccordionSection =
    paymentMethod &&
    !checkServiceAlertsOfPaymentMethodItemDisable({
      serviceAlertsStore,
      paymentMethod,
    });

  const isShowHowToPayButton = [METHOD_DEPOSIT, METHOD_PAYID].includes(
    paymentMethod,
  );

  const isShowSuccessfullyDesc = [METHOD_DEPOSIT, METHOD_PAYID].includes(
    paymentMethod,
  );

  const parseAmountValue = (value) =>
    value ? format.toAmountStr(numberUtils.mathRound(parseFloat(value))) : 0;

  const handleOnToggleInstructionPopup = () => {
    setOpenInstructionPopup(!isOpenInstructionPopup);
  };

  const renderPaymentImage = (cardNumber) => {
    if (
      paymentStore[PAYMENT_CREADIT_CARD_TYPE] === CREADIT_CARD_TYPE_VISA ||
      visaNumberValid(cardNumber)
    ) {
      return isMobile ? (
        <img src={VisaIcon} width={37} height={12} alt="visa" />
      ) : (
        <img src={VisaIcon} width={49.54} height={18} alt="visa" />
      );
    }

    if (
      paymentStore[PAYMENT_CREADIT_CARD_TYPE] ===
        CREADIT_CARD_TYPE_MASTERCARD ||
      masterCardNumberValid(cardNumber)
    ) {
      return isMobile ? (
        <img src={MasterCardIcon} width={37} height={12} alt="master-card" />
      ) : (
        <img src={MasterCardIcon} width={49.54} height={18} alt="master-card" />
      );
    }

    return null;
  };

  const renderLoginToTextReference = {
    [METHOD_PAYID]: t('add_transaction_submitted_form_text_2'),
    [METHOD_DEPOSIT]: t('add_transaction_submitted_form_text_3'),
  };

  const renderSubmitedFormReference = {
    [METHOD_PAYID]: (
      <>
        <FormInputCopy
          field={PAYMENT_EMAIL_FIELD}
          value={isHHMTTheme ? HHMT_EMAIL_PAY : REMOX_EMAIL_PAY}
          inputStyles={{ textTransform: 'lowercase' }}
        />
        <FormInputCopy
          field={PAYMENT_AMOUNT_FIELD}
          value={Number(totalPayAmount)}
        />
        <FormInputCopy field={PAYMENT_REFERENCE_FIELD} value={refNumber} />
      </>
    ),
    [METHOD_DEPOSIT]: (
      <>
        <FormInputCopyHighlight
          field={PAYMENT_ACCOUNT_FIELD}
          value={isHHMTTheme ? HHMT_NAME : REMOX_NAME}
          inputStyles={{ textTransform: 'capitalize' }}
        />
        <FormInputCopy
          field={PAYMENT_BSB_FIELD}
          value={isHHMTTheme ? HHMT_BSB : REMOX_BSB}
        />
        <FormInputCopy
          field={PAYMENT_ACCOUNT_NUMBER_FIELD}
          value={isHHMTTheme ? HHMT_ACCOUNT_NUMBER : REMOX_ACCOUNT_NUMBER}
        />
        <FormInputCopy
          field={PAYMENT_AMOUNT_FIELD}
          value={Number(totalPayAmount)}
        />
        <FormInputCopy field={PAYMENT_REFERENCE_FIELD} value={refNumber} />
      </>
    ),
    [METHOD_EPAYMENT]: (
      <FieldsWrap style={{ marginBottom: isMobile ? '16px' : '32px' }}>
        <FieldWrap styles={{ alignItems: 'start' }}>
          <FieldLabel>{t('label_you_paid')}</FieldLabel>
          <FieldValue>{`${parseAmountValue(totalPayAmount)} AUD`}</FieldValue>
        </FieldWrap>
        <FieldWrap>
          <FieldLabel>{t('label_payment_method')}</FieldLabel>
          <FieldValue>
            {renderPaymentImage(
              cardInformation[PAYMENT_BY_DEPOSIT_NUMBER_NAME],
            )}
            <span className="credit-card">
              {`${t('label_visa_end_with')} ${
                paymentStore[PAYMENT_CREADIT_CARD_NO] ||
                cardInformation[PAYMENT_BY_DEPOSIT_NUMBER_NAME].slice(-4)
              }
              `}
            </span>
          </FieldValue>
        </FieldWrap>
        <FieldWrap>
          <FieldLabel>{t('label_reference_number')}</FieldLabel>
          <FieldValue className="ref-number">{refNumber}</FieldValue>
        </FieldWrap>
      </FieldsWrap>
    ),
    [METHOD_PAYTO]: (
      <FieldsWrap style={{ marginBottom: isMobile ? '16px' : '32px' }}>
        <FieldWrap styles={{ alignItems: 'start' }}>
          <FieldLabel>{t('label_you_paid')}</FieldLabel>
          <FieldValue>{`${parseAmountValue(totalPayAmount)} AUD`}</FieldValue>
        </FieldWrap>
        <FieldWrap styles={{ alignItems: 'start' }}>
          <FieldLabel>{t('label_payment_method')}</FieldLabel>
          <FieldValue>
            <img src={PaytoIcon} width={43} height={24} alt="" />
            <br />
            <span className="bankcode-accountnumber">{paymentMethodInfo}</span>
          </FieldValue>
        </FieldWrap>
        <FieldWrap>
          <FieldLabel>{t('label_reference_number')}</FieldLabel>
          <FieldValue className="ref-number">{refNumber}</FieldValue>
        </FieldWrap>
      </FieldsWrap>
    ),
  };

  const getTransactionStatus = () => {
    if (paymentStatus === STATUS_SUCCESS) {
      return STATUS_PROCESSING;
    }

    return STATUS_SUBMITTED;
  };

  const handleViewYourTransaction = async () => {
    clearTimeout(stopTimeoutRef.current);
    clarityAPI.stop();

    const transactionInformation = await fetchTransactionInformation(
      token,
      refNumber,
      getTransactionStatus(),
    );

    dispatch({
      type: SET_TRANSACTION_DETAILS,
      payload: transactionInformation,
    });

    resetAddTransactionInformation({
      isResetTransactionDetails: false,
    });

    dispatch({
      type: SET_CURRENT_RECEIVER,
      payload: null,
    });

    dispatch({
      type: SET_ADD_TRANSACTION_CURRENT_STEP,
      payload: 0,
    });

    Cookies.remove(MAKE_PAYMENT_KEY);

    navigate(TRANSACTIONS_PREFIX);
  };

  return (
    <>
      {isOpenInstructionPopup && (
        <>
          {isMobile || isTablet ? (
            <InstructionPopupMobile
              isOpen={isOpenInstructionPopup}
              onClose={handleOnToggleInstructionPopup}
              method={paymentMethod}
              isBackdropTransparent={false}
            />
          ) : (
            <InstructionPopup
              isOpen={isOpenInstructionPopup}
              onClose={handleOnToggleInstructionPopup}
              method={paymentMethod}
              isBackdropTransparent={false}
            />
          )}
        </>
      )}

      <PaymentMethodSuccessStyled>
        {isDesktop && <TitleImage src={PaymentSuccessfullyImage} />}
        <TitleSubmitted>
          {stringUtils.replaceKeyword(
            t('add_transaction_submitted_successfully'),
            [
              {
                key: 'refNumber',
                value: refNumber,
              },
            ],
          )}
        </TitleSubmitted>
        {isShowSuccessfullyDesc && (
          <NotiPaySection>
            <img src={NotifyPayIcon} width={50} height={40.59} />
            <Label
              style={{
                fontWeight: 700,
              }}
            >
              {t('add_transaction_submitted_form_text_1')}
            </Label>
          </NotiPaySection>
        )}
        {Boolean(renderLoginToTextReference[paymentMethod]) && (
          <Label style={{ marginBlock: isMobile ? '8px' : '16px' }}>
            {renderLoginToTextReference[paymentMethod]}
          </Label>
        )}
        {renderSubmitedFormReference[paymentMethod]}
        {isShowHowToPayButton && (
          <HowToPay onClick={handleOnToggleInstructionPopup}>
            {stringUtils.replaceKeyword(t('add_transaction_how_to_pay_by'), [
              {
                key: 'paymentMethod',
                value: t(PAYMENT_METHOD_TEXT_REFERENCE[paymentMethod]),
              },
            ])}
            <img
              src={isHHMTTheme ? HowToHHMTIcon : HowToREMOXIcon}
              width={24}
              height={24}
            />
          </HowToPay>
        )}
        <ContactInfoSection
          dangerouslySetInnerHTML={{
            __html: t('add_transaction_submitted_form_contact_info', {
              email: isHHMTTheme ? HHMT_EMAIL : REMOX_EMAIL,
              phone1: isHHMTTheme ? HHMT_PHONE : REMOX_PHONE,
              phone1_format: formatter.mask(
                isHHMTTheme ? HHMT_PHONE : REMOX_PHONE,
                formatter.formatParse(CONTACT_PHONE_NUMBER_FORMAT),
              ),
              phone2: isHHMTTheme ? HHMT_PHONE_2 : REMOX_PHONE_2,
              phone2_format: formatter.mask(
                isHHMTTheme ? HHMT_PHONE_2 : REMOX_PHONE_2,
                formatter.formatParse(CONTACT_PHONE_NUMBER_FORMAT),
              ),
            }),
          }}
        />
        <NextWrap
          style={{
            justifyContent: 'end',
            marginTop: '40px',
          }}
        >
          <ActionWrapper>
            <ButtonCommon
              value={t('button_view_your_transaction')}
              color="var(--c-primary)"
              background="var(--bg-primary)"
              isFill={true}
              styles={{
                margin: '0px',
                marginInline: 'auto',
                width: isMobile ? '100%' : '211px',
              }}
              onClick={handleViewYourTransaction}
              isUseKeyDown
            />
          </ActionWrapper>
        </NextWrap>
      </PaymentMethodSuccessStyled>
    </>
  );
};

const PaymentMethodSuccessStyled = styled.div`
  display: inline-block;
  width: 100%;
  margin-top: 0px;
  padding-inline: 0px;
  padding-bottom: 140px;
  background: var(--ds-c-white);

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    display: block;
    width: 437px;
    margin-top: -8px;
    margin-inline: auto;
    padding-inline: 0px;
    padding-bottom: unset;
  }
`;

const TitleSubmitted = styled.h1`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 700;
  font-size: 20px;
  line-height: 25px;

  color: var(--ds-c-green-default);
  margin-bottom: 8px;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    margin-bottom: 16px;
  }
`;

const Label = styled.p`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;

  color: var(--ds-c-grey-dark);
  padding: 0;
  margin: 0;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 16px;
    line-height: 20px;
  }
`;

const HowToPay = styled.p`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;

  color: var(--ds-c-blue-hyperlink-default);
  padding: 0;
  margin: 0;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-block: 16px;

  & img {
    margin-left: 4px;
  }

  &:hover {
    color: var(--ds-c-blue-hyperlink-hover);

    & img {
      opacity: 0.6;
    }
  }

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 16px;
    line-height: 20px;
    margin-block: 32px;
  }
`;

const ContactInfoSection = styled.div`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: normal;

  color: #0e1012;

  padding: 8px 20px;
  width: 100%;
  height: fit-content;
  background: var(--ds-c-grey-disabled);
  border-radius: 12px;

  & a {
    color: var(--ds-c-blue-hyperlink-default);
    text-decoration: none;
    &:hover {
      color: var(--ds-c-blue-hyperlink-hover);
    }
  }

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 16px;
    line-height: 20px;
  }
`;

const NotiPaySection = styled.div`
  display: flex;
  gap: 10px;
  border-radius: 8px;
  padding: 8px 10px;
  background-color: var(--ds-c-yellow-light);
`;

const TitleImage = styled.img`
  display: block;
  margin-inline: auto;
  margin-bottom: 32px;
`;

const FieldsWrap = styled.div`
  width: 100%;
  height: fit-content;
  padding: 8px 16px;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.1);
  border-radius: 12px;
`;
const FieldWrap = styled.div`
  display: flex;
  align-items: ${(props) => props.styles?.alignItems || 'center'};
  justify-content: start;
  min-height: 40px;
  height: fit-content;
  padding: ${(props) => props.styles?.padding};
`;
const FieldLabel = styled.p`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  color: #0e1012;

  margin: 0;
  padding: 0;
  min-width: 140px;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 16px;
    line-height: 20px;
  }
`;
const FieldValue = styled.p`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
  text-transform: uppercase;
  color: #0e1012;

  width: 100%;
  margin: 0;
  padding: 0;
  padding-left: 10px;

  & span {
    color: #7a8189;
  }

  & span.credit-card {
    margin-left: 8px;
    text-transform: initial;
    color: var(--ds-c-grey-dark);
  }

  & span.bankcode-accountnumber {
    margin-left: 0px;
    text-transform: initial;
    color: var(--ds-c-grey-dark);
  }

  &.ref-number {
    font-family: var(--ff-secondary);
    font-weight: 700;
    color: #002ff1;
  }
`;

export default TransactionSummary;
